/**
 * GPS-instrument: fart over grunnen (SOG) og kurs over grunnen (COG).
 *
 * Bruker nettleserens Geolocation API. Først en rask grov posisjon
 * (getCurrentPosition, nettverk) for å utløse tillatelsen og vise noe raskt,
 * deretter watchPosition med høy nøyaktighet for live GPS.
 *
 * Robusthet: tidsavbrudd (kald GPS / innendørs) stopper IKKE økta — vi
 * fortsetter å søke. Et diagnose-panel viser HTTPS-status, tillatelse og siste
 * hendelse/feil, slik at det er lett å se hvorfor signal evt. mangler.
 *
 * Alt skjer lokalt på telefonen — ingenting sendes til serveren.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GsfLogo } from '../components/Logo.tsx';
import {
  metersBetween,
  bearingDeg,
  msToKnots,
  msToKmh,
  compassPointNo,
  smoothAngle,
  ema,
  type AngleVec,
  type LatLon,
} from '../lib/geo.ts';
import {
  enableWakeLock,
  disableWakeLock,
  isWakeLockActive,
  isScreenAwakeAvailable,
} from '../lib/wakelock.ts';

type Status = 'idle' | 'active' | 'denied' | 'unavailable';

const MIN_SPEED_MS = 0.25; // ~0.5 knop — under dette er kurs udefinert

function errLabel(code: number): string {
  if (code === 1) return 'tilgang avslått';
  if (code === 2) return 'posisjon utilgjengelig';
  if (code === 3) return 'tidsavbrudd';
  return `feil ${code}`;
}

const GOOD_ACCURACY_M = 75; // dårligere enn dette regnes som grov nettverksposisjon, ikke GPS

export function InstrumentPage() {
  const [status, setStatus] = useState<Status>('idle');
  const [searching, setSearching] = useState(false);
  const [wakeLockOn, setWakeLockOn] = useState(false);
  const [sogKn, setSogKn] = useState(0);
  const [sogKmh, setSogKmh] = useState(0);
  const [cog, setCog] = useState<number | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [fixes, setFixes] = useState(0);

  // Diagnostikk
  const secure = typeof window !== 'undefined' ? window.isSecureContext : true;
  const [permState, setPermState] = useState<string>('ukjent');
  const [lastEvent, setLastEvent] = useState<string>('');

  const watchId = useRef<number | null>(null);
  const pollId = useRef<number | null>(null);
  const prevFix = useRef<(LatLon & { t: number }) | null>(null);
  const sogEma = useRef<number | null>(null);
  const cogVec = useRef<AngleVec | null>(null);

  function handleFix(pos: GeolocationPosition) {
    const t = pos.timestamp;
    // Dedup: poll og watch kan levere nøyaktig samme fix.
    if (prevFix.current && t === prevFix.current.t) return;
    setSearching(false);
    const { latitude, longitude, speed, heading, accuracy: acc } = pos.coords;
    const here: LatLon = { lat: latitude, lon: longitude };

    // Fart (m/s)
    let speedMs: number | null =
      speed != null && Number.isFinite(speed) && speed >= 0 ? speed : null;
    if (speedMs === null && prevFix.current) {
      const dt = (t - prevFix.current.t) / 1000;
      if (dt > 0.3) speedMs = metersBetween(prevFix.current, here) / dt;
    }
    if (speedMs === null) speedMs = 0;

    const smoothedMs = ema(sogEma.current, speedMs, 0.4);
    sogEma.current = smoothedMs;
    setSogKn(Math.max(0, msToKnots(smoothedMs)));
    setSogKmh(Math.max(0, msToKmh(smoothedMs)));

    // Kurs (grader)
    if (smoothedMs >= MIN_SPEED_MS) {
      let course: number | null = heading != null && Number.isFinite(heading) ? heading : null;
      if (course === null && prevFix.current) {
        const moved = metersBetween(prevFix.current, here);
        if (moved > Math.max(2, (acc ?? 0) * 0.5)) course = bearingDeg(prevFix.current, here);
      }
      if (course !== null) {
        const r = smoothAngle(cogVec.current, course, 0.35);
        cogVec.current = r.vec;
        setCog(r.angle);
      }
    } else {
      cogVec.current = null;
      setCog(null);
    }

    setAccuracy(acc ?? null);
    prevFix.current = { ...here, t };
    setFixes((n) => n + 1);
    setLastEvent(`fix ±${Math.round(acc)} m${speed != null ? ` · fart fra GPS` : ' · fart beregnet'}`);
  }

  async function queryPermission() {
    try {
      const perms = (navigator as unknown as {
        permissions?: { query: (d: { name: PermissionName }) => Promise<PermissionStatus> };
      }).permissions;
      if (perms?.query) {
        const p = await perms.query({ name: 'geolocation' as PermissionName });
        setPermState(p.state);
        p.onchange = () => setPermState(p.state);
      }
    } catch {
      /* Permissions API ikke tilgjengelig (eldre Safari) */
    }
  }

  function stop() {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    if (pollId.current !== null) {
      clearInterval(pollId.current);
      pollId.current = null;
    }
    prevFix.current = null;
    sogEma.current = null;
    cogVec.current = null;
    void disableWakeLock();
    setWakeLockOn(false);
    setStatus('idle');
    setSearching(false);
  }

  function start() {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }
    setStatus('active');
    setSearching(true);
    setFixes(0);
    setLastEvent('starter…');
    void queryPermission();
    void enableWakeLock().then((ok) => setWakeLockOn(ok && isWakeLockActive()));

    // 1) Rask grov posisjon — utløser tillatelse og gir noe å vise med en gang.
    navigator.geolocation.getCurrentPosition(
      (pos) => handleFix(pos),
      (err) => setLastEvent(`grov: ${errLabel(err.code)}`),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );

    // 2) Live, presis GPS.
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => handleFix(pos),
      (err) => {
        setLastEvent(`GPS: ${errLabel(err.code)}`);
        if (err.code === 1) {
          if (watchId.current !== null) {
            navigator.geolocation.clearWatch(watchId.current);
            watchId.current = null;
          }
          void disableWakeLock();
          setStatus('denied');
          setSearching(false);
          return;
        }
        // Tidsavbrudd / utilgjengelig er som regel forbigående — fortsett å søke.
        setSearching(true);
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 30_000 },
    );

    // Poll som sikrer jevne oppdateringer der watchPosition stopper opp
    // (sett på enkelte Android/Chrome). Leser siste fix hvert 2. sekund.
    pollId.current = window.setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => handleFix(pos),
        (err) => setLastEvent(`poll: ${errLabel(err.code)}`),
        { enableHighAccuracy: true, timeout: 27_000, maximumAge: 1000 },
      );
    }, 3000);
  }

  useEffect(() => {
    void queryPermission();
    return () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
      if (pollId.current !== null) clearInterval(pollId.current);
      void disableWakeLock();
    };
  }, []);

  const active = status === 'active';
  const hasFix = fixes > 0;
  const goodFix = active && hasFix && accuracy != null && accuracy <= GOOD_ACCURACY_M;

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="max-w-md mx-auto px-4 pt-3 pb-5">
        <div className="flex items-center justify-between mb-2">
          <Link to="/" className="text-slate-400 text-sm">&larr; Forsiden</Link>
          <Link to="/" aria-label="Grimstad Seilforening">
            <GsfLogo size="sm" />
          </Link>
        </div>

        <h1 className="text-base font-bold mb-2">Fart &amp; kurs</h1>

        {/* SOG */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 px-4 py-2.5 text-center mb-2.5">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">SOG — fart over grunnen</div>
          <div
            className="font-mono font-bold tabular-nums leading-none"
            style={{ fontSize: 'clamp(2.5rem, 14vw, 4rem)' }}
          >
            {goodFix ? sogKn.toFixed(1) : '—'}
          </div>
          <div className="text-sm text-slate-300">
            knop
            {goodFix && <span className="text-slate-500"> · {sogKmh.toFixed(1)} km/t</span>}
          </div>
        </div>

        {/* COG kompassrose */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 px-4 py-2.5 mb-2.5">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1 text-center">
            COG — kurs over grunnen
          </div>
          <CompassRose cog={goodFix ? cog : null} />
          <div className="text-center mt-1.5">
            <span className="font-mono text-2xl font-bold tabular-nums">
              {goodFix && cog !== null
                ? `${Math.round(cog).toString().padStart(3, '0')}°`
                : '— — —'}
            </span>
            {goodFix && cog !== null && (
              <span className="ml-2 text-slate-400">{compassPointNo(cog)}</span>
            )}
            {goodFix && cog === null && (
              <div className="text-xs text-slate-500 mt-1">For lav fart til å vise kurs</div>
            )}
          </div>
        </div>

        {active && searching && (
          <p className="text-sm text-sky-300 mb-3 text-center">
            📡 Søker etter GPS-signal… Gå gjerne utendørs for bedre dekning.
          </p>
        )}
        {active && hasFix && !goodFix && (
          <div className="text-sm text-amber-300 mb-3 text-center">
            <p>📡 Kun grov posisjon (±{accuracy != null ? Math.round(accuracy) : '?'} m) — ikke ekte GPS.</p>
            <p className="text-xs mt-1 text-amber-200/90">
              ±2000 m betyr som regel at nettleseren bare har «Omtrentlig / Approximate» posisjon. Slå
              på «Nøyaktig posisjon» for nettleseren i telefonens innstillinger, og gå utendørs.
            </p>
          </div>
        )}
        {status === 'denied' && (
          <p className="text-sm text-red-300 mb-3">
            Posisjonstilgang er avslått. Tillat posisjon for dette nettstedet i nettleserens
            innstillinger, og prøv igjen.
          </p>
        )}
        {status === 'unavailable' && (
          <p className="text-sm text-red-300 mb-3">Enheten/nettleseren støtter ikke posisjon.</p>
        )}

        {!active ? (
          <button
            onClick={start}
            className="w-full bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white font-semibold py-3 rounded-xl text-base transition"
          >
            📍 Start GPS
          </button>
        ) : (
          <button
            onClick={stop}
            className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 rounded-xl text-base transition"
          >
            Stopp
          </button>
        )}

        {active && wakeLockOn && (
          <div className="text-xs text-emerald-400 text-center mt-3">📱 Skjermen holdes våken</div>
        )}

        <div className="text-xs text-slate-500 text-center mt-3">
          Kurs = retningen du faktisk beveger deg (ikke der baugen peker).
          {!isScreenAwakeAvailable() && ' Skjermen kan slukke på denne enheten.'}
        </div>

        {/* Diagnose */}
        <div className="mt-3 text-[11px] text-slate-500 border-t border-slate-800 pt-2 space-y-0.5">
          <div className="flex items-center justify-between">
            <span className="uppercase tracking-wide text-slate-600">Diagnose</span>
            <Link to="/hjelp/posisjon" className="text-sky-400 normal-case">
              📍 Hjelp: nøyaktig posisjon
            </Link>
          </div>
          <div>
            Sikker tilkobling (HTTPS):{' '}
            <span className={secure ? 'text-emerald-400' : 'text-red-400'}>
              {secure ? 'ja' : 'NEI — GPS blokkeres uten https'}
            </span>
          </div>
          <div>Posisjonstillatelse: <span className="text-slate-300">{permState}</span></div>
          <div>
            Målinger: <span className="text-slate-300">{fixes}</span>
            {accuracy != null && <span> · nøyaktighet ±{Math.round(accuracy)} m</span>}
          </div>
          {lastEvent && <div>Siste hendelse: <span className="text-slate-300">{lastEvent}</span></div>}
        </div>
      </div>
    </div>
  );
}

function CompassRose({ cog }: { cog: number | null }) {
  const ticks = [];
  for (let d = 0; d < 360; d += 10) {
    const major = d % 30 === 0;
    const rad = (d * Math.PI) / 180;
    const sx = Math.sin(rad);
    const cy = -Math.cos(rad);
    const r1 = 100;
    const r2 = major ? 84 : 92;
    ticks.push(
      <line
        key={d}
        x1={r1 * sx}
        y1={r1 * cy}
        x2={r2 * sx}
        y2={r2 * cy}
        stroke={major ? '#94a3b8' : '#475569'}
        strokeWidth={major ? 2 : 1}
      />,
    );
  }

  const cardinals: Array<[string, number]> = [
    ['N', 0],
    ['Ø', 90],
    ['S', 180],
    ['V', 270],
  ];

  return (
    <svg viewBox="-115 -115 230 230" className="w-full max-w-[200px] mx-auto block">
      <circle cx="0" cy="0" r="100" fill="none" stroke="#334155" strokeWidth="2" />
      {ticks}
      {cardinals.map(([label, d]) => {
        const rad = (d * Math.PI) / 180;
        const x = 70 * Math.sin(rad);
        const y = -70 * Math.cos(rad);
        return (
          <text
            key={label}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="20"
            fontWeight="bold"
            fill={label === 'N' ? '#f87171' : '#cbd5e1'}
          >
            {label}
          </text>
        );
      })}

      {cog !== null && (
        <g transform={`rotate(${cog})`}>
          <polygon points="0,-88 9,0 0,-16 -9,0" fill="#34d399" />
          <polygon points="0,88 6,0 0,16 -6,0" fill="#475569" />
        </g>
      )}
      <circle cx="0" cy="0" r="6" fill="#0ea5e9" stroke="#0b1220" strokeWidth="2" />
    </svg>
  );
}
